import React from 'react';
import { useQuery } from 'react-query';
import {
  UsersIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  ChatBubbleLeftRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';
import { collegeAuthAPI } from '../services/api';

const DashboardPage = () => {
  const { data, isLoading, error, refetch } = useQuery(
    'dashboard',
    collegeAuthAPI.getDashboard,
    {
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  if (isLoading) return <LoadingSpinner text="Loading dashboard..." />;

  if (error) {
    console.error('❌ Dashboard Error:', error);
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">Error loading dashboard data</p>
        <p className="text-sm text-gray-500 mb-4">{error.message}</p>
        <button
          onClick={() => refetch()}
          className="btn-primary"
        >
          Try Again
        </button>
      </div>
    );
  }

  const { statistics, recentUsers, recentClubs, branchStats } = data?.data?.data || {};

  const statCards = [
    {
      name: 'Total Students',
      value: statistics?.totalStudents || 0,
      icon: UsersIcon,
      color: 'bg-blue-500',
      change: '+12%',
      changeType: 'increase',
    },
    {
      name: 'Total Faculty',
      value: statistics?.totalFaculty || 0,
      icon: AcademicCapIcon,
      color: 'bg-green-500',
      change: '+3%',
      changeType: 'increase',
    },
    {
      name: 'Active Clubs',
      value: statistics?.totalClubs || 0,
      icon: BuildingOfficeIcon,
      color: 'bg-purple-500',
      change: '+8%',
      changeType: 'increase',
    },
    {
      name: 'Pending Applications',
      value: statistics?.activeApplications || 0,
      icon: ChatBubbleLeftRightIcon,
      color: 'bg-yellow-500',
      change: '-2%',
      changeType: 'decrease',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Welcome to Your College Dashboard
          </h3>
          <div className="mt-2 max-w-xl text-sm text-gray-500">
            <p>
              Monitor and manage all aspects of your college community from this central hub.
            </p>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <div key={stat.name} className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`${stat.color} p-3 rounded-md`}>
                  <stat.icon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {stat.name}
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {stat.value.toLocaleString()}
                    </div>
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                      stat.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {stat.changeType === 'increase' ? (
                        <ArrowUpIcon className="self-center flex-shrink-0 h-4 w-4" />
                      ) : (
                        <ArrowDownIcon className="self-center flex-shrink-0 h-4 w-4" />
                      )}
                      <span className="sr-only">
                        {stat.changeType === 'increase' ? 'Increased' : 'Decreased'} by
                      </span>
                      {stat.change}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <div className="card">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Recent Registrations
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Latest student and faculty registrations
            </p>
          </div>
          <div className="px-4 py-5 sm:p-6">
            {recentUsers && recentUsers.length > 0 ? (
              <div className="space-y-4">
                {recentUsers.map((user) => (
                  <div key={user._id} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`badge ${
                        user.role === 'Student' ? 'badge-blue' : 'badge-green'
                      }`}>
                        {user.role}
                      </span>
                      <span className={`badge ${
                        user.isVerified ? 'badge-green' : 'badge-yellow'
                      }`}>
                        {user.isVerified ? 'Approved' : 'Pending'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No recent registrations</p>
            )}
          </div>
        </div>

        {/* Recent Clubs */}
        <div className="card">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Recent Clubs
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Latest club creations
            </p>
          </div>
          <div className="px-4 py-5 sm:p-6">
            {recentClubs && recentClubs.length > 0 ? (
              <div className="space-y-4">
                {recentClubs.map((club) => (
                  <div key={club._id} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-lg">{club.logo}</span>
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">{club.name}</p>
                        <p className="text-sm text-gray-500">
                          Mentor: {club.facultyMentor?.name}
                        </p>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {club.membersCount || 0} members
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No recent clubs</p>
            )}
          </div>
        </div>
      </div>

      {/* Branch Statistics */}
      {branchStats && branchStats.length > 0 && (
        <div className="card">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Branch-wise Statistics
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Distribution of students and faculty across branches
            </p>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {branchStats.map((branch) => (
                <div key={branch._id} className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">{branch._id}</h4>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Students:</span>
                      <span className="font-medium">{branch.students}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Faculty:</span>
                      <span className="font-medium">{branch.faculty}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;